import { h } from 'preact';
import { Text } from 'preact-i18n';

import {
    badgeFor, formatDateNo, colorForType, championshipFor, groupRowsByDay, selectRowHandler,
} from '../../lib/nsfCompetitionUi';
import { RsvpAvatars, RsvpMark } from './rsvp';

const noRsvp = () => undefined;

// Terminliste list: a table from md up, day-grouped cards with sticky day headers
// below. One entry per mirrored NSF competition. Pure props → markup.
function Badge({ kind }) {
    if (kind === 'cancelled') {
        return <span class='badge text-bg-danger ms-2'><Text id='terminliste.cancelled'>Cancelled</Text></span>;
    }
    if (kind === 'new') {
        return <span class='badge text-bg-success ms-2'><Text id='terminliste.new'>New</Text></span>;
    }
    if (kind === 'changed') {
        return <span class='badge text-bg-warning ms-2'><Text id='terminliste.changed'>Changed</Text></span>;
    }
    return null;
}

export function Championship({ typeName }) {
    const label = championshipFor(typeName);
    if (!label) {
        return null;
    }
    return (
        <span class='badge text-bg-warning ms-1 align-middle'>
            <i class='fa-duotone fa-trophy me-1' />
            {label}
        </span>
    );
}

function timeNo(value) {
    const text = formatDateNo(value);
    return text ? text.slice(-5) : '';
}

function Card({
    row, now, withDistance, onSelect, rsvps, onOpenProfile,
}) {
    const badge = badgeFor(row, now);
    const cancelled = badge === 'cancelled';
    const summary = rsvps(row.id);
    return (
        <a
            href={`/terminliste/${row.id}`}
            class={`list-group-item list-group-item-action px-2 py-2 ${cancelled ? 'opacity-75' : ''}`}
            onClick={selectRowHandler(onSelect, row)}
        >
            <div class='d-flex align-items-start gap-2'>
                {summary && summary.mine && <span class='flex-shrink-0'><RsvpMark mine={summary.mine} /></span>}
                <span class='d-inline-block rounded-circle flex-shrink-0 mt-1' style={`width:.7rem;height:.7rem;background:${colorForType(row.competitionTypeName)}`} />
                <div class='flex-grow-1 min-width-0'>
                    <div class={`fw-semibold ${cancelled ? 'text-decoration-line-through text-body-secondary' : ''}`}>
                        {row.title}
                        <Championship typeName={row.competitionTypeName} />
                        <Badge kind={badge} />
                        <RsvpAvatars summary={summary} onOpenProfile={onOpenProfile} />
                    </div>
                    <div class='small text-body-secondary'>{row.organizationName}{row.kretsName ? ` · ${row.kretsName}` : ''}</div>
                    <div class='small text-body-secondary'>
                        {row.competitionTypeName}
                        {row.competitionNumber > 0 && <span class='text-body-tertiary ms-1'>#{row.competitionNumber}</span>}
                    </div>
                </div>
                <div class='text-end small text-nowrap flex-shrink-0'>
                    <div>{timeNo(row.startDate)}</div>
                    {withDistance && Number.isFinite(row.distanceKm) && <div class='text-body-secondary'>{row.distanceKm} km</div>}
                </div>
            </div>
        </a>
    );
}

function NsfCompetitionList({
    rows = [], now = new Date(), showDistance, onSelect, rsvps = noRsvp, onOpenProfile,
}) {
    if (!rows || rows.length === 0) {
        return (
            <div class='alert alert-light border text-body-secondary'>
                <Text id='terminliste.noRows'>No competitions match these filters.</Text>
            </div>
        );
    }
    const withDistance = showDistance === undefined ? rows.some(r => Number.isFinite(r.distanceKm)) : showDistance;
    const days = groupRowsByDay(rows);
    return (
        <div>
        <div class='d-md-none'>
            {days.map(day => (
                <div key={day.iso}>
                    <div class='position-sticky top-0 bg-body small fw-semibold text-body-secondary px-2 py-1 border-bottom' style='z-index: 1;'>
                        {day.label}
                    </div>
                    <div class='list-group list-group-flush'>
                        {day.rows.map(row => (
                            <Card
                                key={row.id}
                                row={row}
                                now={now}
                                withDistance={withDistance}
                                onSelect={onSelect}
                                rsvps={rsvps}
                                onOpenProfile={onOpenProfile}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
        <div class='d-none d-md-block table-responsive'>
            <table class='table table-sm table-hover align-middle mb-0'>
                <thead>
                    <tr>
                        <th class='pe-0' style='width: 1.25rem;' />
                        <th><Text id='terminliste.name'>Name</Text></th>
                        <th><Text id='terminliste.date'>Date</Text></th>
                        <th class='d-none d-md-table-cell'><Text id='terminliste.organizer'>Organiser</Text></th>
                        <th class='d-none d-lg-table-cell'><Text id='terminliste.type'>Type</Text></th>
                        <th class='d-none d-lg-table-cell'><Text id='terminliste.krets'>Krets</Text></th>
                        {withDistance && <th class='text-end'><Text id='terminliste.distance'>Distance</Text></th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const badge = badgeFor(row, now);
                        const cancelled = badge === 'cancelled';
                        const titleClass = cancelled ? 'text-decoration-line-through text-body-secondary' : '';
                        const summary = rsvps(row.id);
                        return (
                            <tr key={row.id} class={cancelled ? 'opacity-75' : ''}>
                                <td class='pe-0 text-center'>
                                    {summary && summary.mine ? <RsvpMark mine={summary.mine} /> : null}
                                </td>
                                <td>
                                    <a
                                        href={`/terminliste/${row.id}`}
                                        class={`fw-semibold ${titleClass}`}
                                        onClick={selectRowHandler(onSelect, row)}
                                    >
                                        {row.title}
                                    </a>
                                    <Championship typeName={row.competitionTypeName} />
                                    <Badge kind={badge} />
                                    <RsvpAvatars summary={summary} onOpenProfile={onOpenProfile} />
                                    <div class='d-md-none small text-body-secondary'>{row.organizationName}</div>
                                    {row.competitionNumber > 0 && <div class='small text-body-tertiary'>#{row.competitionNumber}</div>}
                                </td>
                                <td class='text-nowrap'>{formatDateNo(row.startDate)}</td>
                                <td class='d-none d-md-table-cell'>{row.organizationName}</td>
                                <td class='d-none d-lg-table-cell'>
                                    <span class='d-inline-block rounded-circle me-1 align-middle' style={`width:.6rem;height:.6rem;background:${colorForType(row.competitionTypeName)}`} />
                                    {row.competitionTypeName}
                                </td>
                                <td class='d-none d-lg-table-cell'>{row.kretsName || ''}</td>
                                {withDistance && (
                                    <td class='text-end text-nowrap'>
                                        {Number.isFinite(row.distanceKm)
                                            ? `${row.distanceKm} km`
                                            : <small class='text-body-tertiary'><Text id='terminliste.noCoordinates'>No map position</Text></small>}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        </div>
    );
}

export default NsfCompetitionList;
